import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Check } from "lucide-react";
import { api, formatMoney, formatDate, formatApiError } from "@/lib/api";

// Modal that turns a week from the year-view into a public, shareable link.
// User optionally adds a note ("hey, next week is going to be tight — heads up"),
// clicks Create link, and gets a stable URL they can copy to a partner.
// The link renders as a proper full page at /s/:snapId (no login needed).
export default function ShareWeekModal({ week, onClose, onCreated }) {
  const [note, setNote] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [creating, setCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/snapshots/tight-week", {
        week_start: week.week_start,
        week_end: week.week_end,
        status: week.status,
        end_balance: Number(week.end_balance || 0),
        bill_count: Number(week.bill_count || 0),
        note: note.trim() || null,
        recipient_email: recipientEmail.trim() || null,
        recipient_name: recipientName.trim() || null,
      });
      const url = `${window.location.origin}/s/${data.id}`;
      setShareUrl(url);
      // Push the new snapshot up to Dashboard so the "N views" pill shows
      // immediately (starts at 0 views until someone opens the link).
      if (onCreated && data.snapshot) onCreated(data.snapshot);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API — select the input.
      const el = document.getElementById("share-url-input");
      if (el) { el.select(); document.execCommand("copy"); setCopied(true); }
    }
  };

  const isShort = week.status === "short";
  const label = isShort ? "Short week" : week.status === "tight" ? "Tight week" : "Safe week";

  return (
    <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="card-soft p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-week-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#4A6148]">Share this week</div>
            <h3 className="font-heading text-xl text-[#2C302E] mt-1">
              {label} · {formatDate(week.week_start)} → {formatDate(week.week_end)}
            </h3>
            <p className="text-xs text-[#59605C] mt-1">
              Projected end balance <span className="font-semibold">{formatMoney(week.end_balance)}</span>
              {week.bill_count > 0 && ` · ${week.bill_count} ${week.bill_count === 1 ? "bill" : "bills"} due`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A918D] hover:text-[#2C302E]" data-testid="share-week-close">
            <X size={18} />
          </button>
        </div>

        {!shareUrl ? (
          <>
            <label className="block">
              <span className="text-xs font-semibold uppercase text-[#59605C]">Add a note (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={400}
                rows={3}
                placeholder="e.g. heads up — next week is going to be really tight. Can we hold off on the streaming subscription?"
                className="input-soft mt-1 resize-none"
                data-testid="share-week-note"
              />
              <span className="text-[11px] text-[#8A918D] mt-1 block">{note.length}/400 · The person you send this to sees only the week + your note. No account access.</span>
            </label>

            {/* Optional recipient details — powers the 7-day follow-up nudge.
                Leaving these blank still creates a valid share link; the
                nudge simply doesn't fire. */}
            <details className="mt-4 group" data-testid="share-week-nudge-toggle">
              <summary className="cursor-pointer text-xs font-semibold text-[#4A6148] hover:text-[#3B5B3E] select-none list-none flex items-center gap-1.5">
                <span className="group-open:rotate-90 inline-block transition-transform">▸</span>
                Nudge them a week after they open it (optional)
              </summary>
              <div className="mt-3 space-y-2 pl-4 border-l-2 border-[#EBE6E0]">
                <p className="text-[11px] text-[#8A918D] leading-relaxed">
                  Enter their email and we&rsquo;ll send one friendly follow-up 7 days after they first open the link — with the same summary + a &ldquo;ask for a fresh one&rdquo; button. One email only, no ongoing spam.
                </p>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase text-[#59605C]">Their name</span>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Sam"
                    className="input-soft mt-1"
                    data-testid="share-week-recipient-name"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase text-[#59605C]">Their email</span>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="sam@example.com"
                    className="input-soft mt-1"
                    data-testid="share-week-recipient-email"
                  />
                </label>
              </div>
            </details>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button
                type="button"
                onClick={create}
                disabled={creating}
                className="btn-primary"
                data-testid="share-week-create"
              >
                {creating ? "Creating…" : "Create share link"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl bg-[#EEF3EC] border border-[#B8CFB1] p-3 flex items-center gap-2" data-testid="share-week-url-row">
              <input
                id="share-url-input"
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent text-sm text-[#2C302E] outline-none min-w-0"
                onFocus={(e) => e.target.select()}
                data-testid="share-week-url"
              />
              <button
                type="button"
                onClick={copy}
                className="btn-primary !py-1.5 !px-3 text-xs shrink-0"
                data-testid="share-week-copy"
              >
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-[#59605C] mt-3 leading-relaxed">
              Anyone with this link can view the week — no login needed. The
              link auto-expires after 30 days so old shares don&rsquo;t linger.
            </p>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className="btn-secondary" data-testid="share-week-done">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
